import React, {useState, useMemo} from 'react'
import {getPriceForCountry} from '../../utils/getPriceForCountry'
import {ProductGroupCard} from '../ProductGroupCard'
import {CARD_VARIANTS} from '../../utils/cardVariants'
import {buildProduct} from './DirectStepperCard'

// Owns the selected option state and add/update logic for a "direct options
// selector" addon card — a dropdown lets the user pick a tier before the
// product is added to the order. Mirrors the DirectStepperCard pattern.
export function DirectOptionsSelectorCard({
  plan,
  group,
  groupPlanCodes,
  selectedCountry,
  selectedProduct,
  optionsConfig,
  isSelected,
  isDimmed,
  onSelect,
  onAddProduct,
  onUpdateProduct,
}) {
  const [userSelectedOption, setUserSelectedOption] = useState(null)
  // Captured once at mount — alreadyIncluded disappears after the first pick,
  // so reading it live would lose the floor on the first selection.
  const [floorPrice] = useState(() => {
    if (!selectedProduct?.alreadyIncluded) return 0
    const price = selectedProduct?.price
    if (price == null) return 0
    const match =
      optionsConfig.find(o => o.price === price) ??
      optionsConfig.find(o => o.price >= price)
    return match?.price ?? 0
  })
  const availableOptions =
    floorPrice > 0
      ? optionsConfig.filter(o => o.price >= floorPrice)
      : optionsConfig

  // Pre-selects the matching option from the existing subscription price.
  // Falls back to price matching for alreadyIncluded items that carry no
  // configuredOptions (e.g. Recurly add-ons in the upgrade flow).
  const derivedOption = useMemo(() => {
    const savedValue = selectedProduct?.configuredOptions?.optionValue
    if (savedValue != null) {
      return optionsConfig.find(o => o.value === savedValue) ?? null
    }
    if (selectedProduct?.price != null) {
      return optionsConfig.find(o => o.price === selectedProduct.price) ?? null
    }
    return null
  }, [selectedProduct, optionsConfig])

  // On X-click, resolveItemsAfterRemove restores the Recurly subscription
  // (alreadyIncluded: true) instead of removing it — skip userSelectedOption
  // so the dropdown reverts to the Recurly-sourced derivedOption.
  const selectedOption = selectedProduct?.alreadyIncluded
    ? derivedOption
    : userSelectedOption ?? derivedOption

  const handleOptionChange = option => {
    setUserSelectedOption(option)
    if (!option) return
    const product = {
      ...buildProduct(plan, group, selectedCountry),
      price: option.price,
      configuredOptions: {optionValue: option.value},
    }
    if (!selectedProduct) {
      onAddProduct(product, groupPlanCodes, group.id)
    } else {
      onUpdateProduct?.(product, group.id)
    }
  }

  const displayPrice =
    selectedOption?.price ??
    getPriceForCountry(plan.currencies, selectedCountry)

  return (
    <ProductGroupCard
      name={plan.name}
      description={plan.description}
      price={displayPrice}
      showPrice={!!selectedOption}
      selectedCountry={selectedCountry}
      billingPeriod={
        plan.productBillingType === 'Annual' ? 'annual' : 'monthly'
      }
      isSelected={isSelected}
      isAdded={!!selectedProduct}
      isDimmed={isDimmed}
      cardVariant={CARD_VARIANTS.DIRECT_OPTIONS_SELECTOR}
      options={availableOptions}
      selectedOption={selectedOption}
      onOptionChange={handleOptionChange}
      onSelect={onSelect}
    />
  )
}
